import React, { useCallback, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import CommonButton from '../../CommonButton';
import { CommonButtonVariants } from '../../../../constants/CommonButtonVariants';
import {
  AUCTION_BID_MAX_BIDS_LIMIT,
  AUCTION_BID_MINIMUM_AMOUNT_IN_PENCE,
  AUCTION_MINIMUM_START_BID_AMOUNT_IN_PENCE,
} from '../../../../constants/AuctionBid';
import { validateBidAmount } from '../../../helpers/AuctionBidHelper';
import { prepareBidsData } from '../../../helpers/prepareBidsData';
import { pencesToPounds } from '../../../helpers/pencesToPounds';
import { fetchAuctionBids } from '../../../../search/SearchActions';
import { preparePriceFormat } from '../../../helpers/preparePriceFormat';
import '../../../../assets/css/componentStyles/BidModal.css';
import SettingsSectionHeader from '../../../../settings/SettingsSectionHeader';
import placeBidIcon from '../../../../assets/img/place-bid-icon.png';
import FormCurrencyInputFieldFixed from '../../formFields/FormCurrencyInputFieldFixed';

function SearchPlaceBidModal({ item, toggleShow, toggleStep }) {
  const dispatch = useDispatch();
  const [validationRequiredBid, setValidationRequiredBid] = useState(null);
  let auctionBids = useSelector((state) => state.search.get('auctionBids'));
  const organizationId = useSelector((state) => state.auth.get('organizationId'));

  useEffect(() => {
    if (auctionBids?.length) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      auctionBids = auctionBids.sort((a, b) => a.amountInPence - b.amountInPence);
    }
  }, [auctionBids]);

  useEffect(() => {
    dispatch(fetchAuctionBids(item?.id));
  }, [dispatch, item]);

  const isBidAllowed = item?.bids?.length < AUCTION_BID_MAX_BIDS_LIMIT;
  const { highestBidMyOrganization } = prepareBidsData(item.bids, organizationId);

  const validationSchema = Yup.object().shape({
    amount: Yup.number()
      .required('Amount value is required')
      .integer('Bid must be an integer')
      .test(
        'required bid amount',
        `Your bid must be greater than or equal to ${preparePriceFormat(
          validationRequiredBid,
        )}`,
        (value) =>
          validateBidAmount(value, item, highestBidMyOrganization, setValidationRequiredBid),
      ),
  });

  const handlePlaceBid = (values, { setSubmitting }) => {
    setSubmitting(false);
    toggleStep(+values.amount);
  };

  const getInitBidValue = useCallback(() => {
    const isCurrentBidderWinning = item.bids.some((it) => it.isHighest);
    if (item?.proxyBidInPence && !isCurrentBidderWinning) {
      return item.proxyBidInPence / 100 + pencesToPounds(AUCTION_BID_MINIMUM_AMOUNT_IN_PENCE);
    }
    if (item?.highestBidInPence) {
      return (
        item.highestBidInPence / 100 + pencesToPounds(AUCTION_BID_MINIMUM_AMOUNT_IN_PENCE)
      );
    }
    // enforce platform minimum first bid (£150) when there are no bids
    const platformMinStartInPence =
      AUCTION_MINIMUM_START_BID_AMOUNT_IN_PENCE + AUCTION_BID_MINIMUM_AMOUNT_IN_PENCE;
    if (item?.minimumPriceInPence != null) {
      const effectiveMin = Math.max(item.minimumPriceInPence, platformMinStartInPence);
      return pencesToPounds(effectiveMin);
    }
    return pencesToPounds(platformMinStartInPence);
  }, [item]);

  const bidInitValue = getInitBidValue();

  return (
    <Modal
      isOpen={true}
      backdrop="static"
      keyboard={false}
      zIndex={3001}
      size="lg"
      className="place-bid-modal"
      backdropClassName="place-bid-modal-backdrop"
    >
      <ModalBody>
        <Formik
          initialValues={{ amount: '' }}
          onSubmit={handlePlaceBid}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {() => (
            <Form autoComplete="off" className="text-left">
              <ModalHeader toggle={toggleShow} tag="div">
                <SettingsSectionHeader header="Place a bid" variant="noBorder" />
              </ModalHeader>
              <p className="bid-content-max-bid">Enter your maximum bid:</p>
              <p className="bid-content-car-title">
                {item.vehicle.vehicleModel.makeName} ${item.vehicle.vehicleModel.name}
              </p>
              <p className="bid-content-car-description">{item.vehicle.derivative}</p>
              <FormCurrencyInputFieldFixed
                maxLength={14}
                name="amount"
                placeholder={`Minimum ${preparePriceFormat(bidInitValue, false)}`}
                label="Bid"
                disabled={!isBidAllowed}
                labelVariant="bordered"
              />
              <div className="actions-wrapper">
                <CommonButton
                  variant={CommonButtonVariants.RED}
                  label="Place bid"
                  type="submit"
                  disabled={!isBidAllowed}
                  className="w-100"
                  icon={placeBidIcon}
                  iconAlt="Place Bid"
                  iconLeft
                />
                <CommonButton
                  variant={CommonButtonVariants.OUTLINED_RED}
                  label="Cancel"
                  handleClick={toggleShow}
                  className="w-100"
                />
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
}

export default SearchPlaceBidModal;
